import React, { useState, useRef, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { collection, query, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import SideMenu from "./SideMenu";
import { useSpecialism, specialismCollectionMap } from "../contexts/SpecialismContext"; // <-- Import context

const navBarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  width: "100%",
  zIndex: 3000,
  background: "#1976d2",
  color: "#fff",
  height: "56px",
  boxShadow: "0 2px 8px rgba(25,118,210,0.08)",
  padding: "0 20px",
  fontFamily: "Roboto, Arial, sans-serif",
};

const navTitleStyle: React.CSSProperties = {
  fontWeight: "bold",
  fontSize: "1.25rem",
  letterSpacing: "0.5px",
  color: "#fff",
};

const navIconButton: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "#fff",
  fontSize: "1.8rem",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  marginRight: 8,
};

const searchBarGridStyle: React.CSSProperties = {
  gridColumn: "1 / -1", // Span all columns
  justifySelf: "center",
  zIndex: 2,
  position: "relative",
  width: "100%",
  maxWidth: 400,
  display: "flex",
  alignItems: "center",
};

const searchBarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  maxWidth: 400, // Add this line
  background: "#fff",
  borderRadius: "24px",
  boxShadow: "0 1px 4px rgba(25,118,210,0.07)",
  padding: "0 8px",
  boxSizing: "border-box",
  height: "40px",
  minHeight: "40px",
  minWidth: 300
};

const searchInputStyle: React.CSSProperties = {
  flex: 1,
  boxSizing: "border-box",
  padding: "8px 8px",
  borderRadius: "20px",
  border: "none",
  outline: "none",
  fontSize: "1rem",
  background: "transparent",
  minWidth: 0,
  color: "#222",
};

const clearButtonStyle: React.CSSProperties = {
  background: "none",
  color: "#888",
  border: "none",
  borderRadius: "50%",
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  marginLeft: "2px",
  transition: "background 0.2s",
};

const searchButtonStyle: React.CSSProperties = {
  background: "#1976d2",
  color: "#fff",
  border: "none",
  borderRadius: "50%",
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 1px 4px rgba(25,118,210,0.10)",
  marginLeft: "4px",
  transition: "background 0.2s",
};

const resultsDropdownStyle: React.CSSProperties = {
  position: "absolute",
  top: "56px",
  left: 0,
  width: "100%",
  maxWidth: 300, // Limit width on desktop
  background: "#fff",
  color: "#222",
  borderRadius: "0 0 16px 16px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
  zIndex: 2000,
  maxHeight: "320px",
  overflowY: "auto",
  padding: "8px 0",
  margin: "0 auto",
  right: 0, // Add this to allow centering
};

type Article = {
  id: string;
  title: string;
  summary: string;
  category: string;
  [key: string]: any;
};

const NavBar: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [input, setInput] = useState("");
  const [results, setResults] = useState<Article[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const { specialismCode, specialism } = useSpecialism();

  // Responsive title logic
  const getNavTitle = () => {
    if (window.innerWidth <= 800) {
      // Mobile: show code
      return (
        <span style={navTitleStyle}>
          PP / {specialismCode}
        </span>
      );
    }
    // Desktop: show full name
    return (
      <span style={navTitleStyle}>
        Pocketpedia / {specialism}
      </span>
    );
  };

  // Listen for resize to force re-render on device change
  const [, setScreenWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (searchTerm.trim().length === 0) {
      setResults([]);
      return;
    }
    const fetchResults = async () => {
      // Use the correct collection for the selected specialism
      const collectionName = specialismCollectionMap[specialismCode] || "articles";
      const q = query(collection(db, collectionName));
      const snapshot = await getDocs(q);
      const lowerTerm = searchTerm.toLowerCase();
      setResults(
        snapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.title || "",
              summary: data.summary || "",
              category: data.category || "",
              ...data,
            };
          })
          .filter(
            article =>
              article.title.toLowerCase().includes(lowerTerm) ||
              article.summary.toLowerCase().includes(lowerTerm)
          )
      );
    };
    fetchResults();
  }, [searchTerm, specialismCode]); // <-- Re-run when specialism changes

  // Hide results when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!searchRef.current?.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    if (showResults) {
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }
  }, [showResults]);

  // Responsive styles for mobile
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @media (max-width: 600px) {
        nav {
          padding-left: 20px !important;
          padding-right: 20px !important;
          box-sizing: border-box !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: hidden !important;
        }
        .navbar-searchbar {
          max-width: 100% !important;
          width: 75% !important;
          margin-left: -50px;
          min-width: 0 !important;
          left: 0;
          right: 0;
          border-radius: 16px !important;
          box-sizing: border-box !important;
        }
        .navbar-search-results {
          position: fixed !important;
          top: 56px !important; 
          left: 0 !important;
          right: 0 !important;
          width: 100vw !important;
          max-width: 100vw !important;
          border-radius: 0 0 16px 16px !important;
          box-sizing: border-box !important;
          z-index: 2002 !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const sideSectionStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    minWidth: 180, // Adjust to match the width of your left section
    justifyContent: "flex-start",
  };

  const centerSectionStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "none", // Remove flex: 1
  };

  // Example menu items
  const menuItems = [
    { label: "Home", onClick: () => history.push("/") },
    { label: "Edit Articles", onClick: () => history.push("/editing") },
    // Add more items as needed
  ];

  return (
    <>
      <nav style={navBarStyle}>
        {/* Left: menu + title */}
        <div style={sideSectionStyle}>
          <button
            style={navIconButton}
            aria-label="Menu"
            onClick={() => setSideMenuOpen(true)} // <-- Open menu on click
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <rect y="4" width="24" height="2" rx="1" fill="currentColor" />
              <rect y="11" width="24" height="2" rx="1" fill="currentColor" />
              <rect y="18" width="24" height="2" rx="1" fill="currentColor" />
            </svg>
          </button>
          <Link to="/" style={{ textDecoration: "none" }}>
            {getNavTitle()}
          </Link>
        </div>
        {/* Center: search bar */}
        <div style={centerSectionStyle} ref={searchRef}>
          <div className="navbar-searchbar" style={searchBarStyle}>
            <input
              type="text"
              value={input}
              onChange={e => {
                setInput(e.target.value);
                setShowResults(!!e.target.value);
                setSearchTerm(e.target.value);
              }}
              onFocus={() => setShowResults(!!input)}
              placeholder="Search articles..."
              style={searchInputStyle}
            />
            {input && (
              <button
                onClick={() => {
                  setInput("");
                  setSearchTerm("");
                  setShowResults(false);
                }}
                style={clearButtonStyle}
                aria-label="Clear search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="20"
                  width="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="5" x2="15" y2="15" />
                  <line x1="15" y1="5" x2="5" y2="15" />
                </svg>
              </button>
            )}
            <button
              onClick={() => {
                setSearchTerm(input);
                setShowResults(!!input);
              }}
              style={searchButtonStyle}
              aria-label="Search"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="22"
                width="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
          </div>
          {showResults && results.length > 0 && (
            <div className="navbar-search-results" style={resultsDropdownStyle}>
              {results.map(article => (
                <div
                  key={article.id}
                  style={{
                    padding: "10px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid #eee",
                    fontSize: "1rem",
                  }}
                  onClick={() => {
                    setShowResults(false);
                    setInput("");
                    setSearchTerm("");
                    history.push(`/${specialismCode}/${article.id}`); // <-- Use specialismCode in route
                  }}
                >
                  <strong>{article.title}</strong>
                  <div style={{ fontSize: "0.9rem", color: "#666" }}>
                    {article.summary.slice(0, 60)}...
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Right: empty but same width as left */}
        <div style={sideSectionStyle} />
      </nav>
      <div
        style={{
          height: "56px",
          width: "100%",
          minHeight: "56px",
        }}
        aria-hidden="true"
      />
      {/* SideMenu overlay */}
      <SideMenu
        open={sideMenuOpen}
        onClose={() => setSideMenuOpen(false)}
        menuItems={menuItems}
      />
    </>
  );
};

export default NavBar;